// IncompleteOrdersPage.js
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchIncompleteOrders, updateOrderStatus, deleteOrder, fetchIncompleteOrdersSummary, checkDuplicatePhoneCount } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle, teePlusPackedItemData } from "../../utils/orderLineDisplay";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import EditOrderModal from "./EditOrderModal";
import { copyOrderToTeePlus } from "../../utils/productionSource";
import {
  ChevronRight,
  ChevronLeft,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  Copy,
  CopyCheck,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Layers,
  Filter,
  Hash,
  Save,
  Plus,
  Minus,
  Search,
  Loader,
  Printer,
  Building2,
  CheckSquare,
  CheckCircle,
  Send,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const IncompleteOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [designFilter, setDesignFilter] = useState("all"); // "all" | "custom" | "normal"
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [isMarkingGirabatcom, setIsMarkingGirabatcom] = useState(false);
  const [isMarkingOld, setIsMarkingOld] = useState(false);
  const [currentReference, setCurrentReference] = useState("Girabatcom.16000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.16000");

  const { data: incompleteOrdersSummary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ["incompleteOrdersSummary"],
    queryFn: fetchIncompleteOrdersSummary,
    enabled: canViewOrders(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);
  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const isLoading = isLoadingSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        // toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        // toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const readyUnits = await readyOrderUnits(detail);
          if (readyUnits) {
            enrichedItems.push(...readyUnits);
            continue;
          }
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM") 
              ? `${API_BASE_URL_V2}${design.driveLink}` 
              : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "غير معروف",
          state: order.address?.state || "غير معروف",
          city: order.address?.city || "",
          phoneNumber: order.phoneNumber || "غير معروف",
          phoneNumber2: order.phoneNumber2 || "غير معروف",
          name: order.name || "غير معروف",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
          status: order.status || "غير معروف",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchIncompleteOrders(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchIncompleteOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    const reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        setSelectedOrderNumbers((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) {
        visibleOrders.forEach((o) => next.add(o.orderNumber));
      } else {
        visibleOrders.forEach((o) => next.delete(o.orderNumber));
      }
      return next;
    });
  };

  const handleCopySelectedPhones = () => {
    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const phones = selected
      .map((o) => (o.phoneNumber || "").trim())
      .filter((phone) => phone && phone !== "غير معروف");

    if (phones.length === 0) {
      toast.info("لا يوجد أرقام موبايل للنسخ");
      return;
    }

    const text = phones.join("\n");
    copyToClipboard(text);
    toast.success(`تم نسخ ${phones.length} رقم موبايل`);
  };

  const handleMarkSelectedAsGirabatcom = async () => {
    if (!canAct) return;

    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const confirmed = window.confirm(
      `هل تريد تحويل ${selected.length} طلب(ات) محددة إلى "Girabatcom"؟\n\nستختفي من الصفحة فورًا.`
    );
    if (!confirmed) return;

    setIsMarkingGirabatcom(true);
    let successCount = 0;
    let failedCount = 0;
    const succeededIds = [];

    toast.info("جاري تحويل المحدد إلى Girabatcom... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-girabatcom",
    });

    for (const order of selected) {
      try {
        await updateOrderStatus(
          order.orderNumber,
          "Girabatcom",
          "تم التحويل لـ Girabatcom من Incomplete"
        );
        succeededIds.push(order.orderNumber);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    if (succeededIds.length > 0) {
      setLoadedOrders((prev) =>
        prev.filter((order) => !succeededIds.includes(order.orderNumber))
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    toast.dismiss("marking-girabatcom");
    setIsMarkingGirabatcom(false);

    if (successCount === 0) {
      toast.error(
        `فشل تحويل الطلبات إلى Girabatcom` +
          (failedCount > 0 ? ` (${failedCount} طلب)` : "")
      );
      return;
    }

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى Girabatcom بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const handleMarkSelectedAsOld = async () => {
    if (!canAct) return;

    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const confirmed = window.confirm(
      `هل تريد تحويل ${selected.length} طلب(ات) محددة إلى "قديم"؟\n\nستختفي من الصفحة فورًا.`
    );
    if (!confirmed) return;

    setIsMarkingOld(true);
    let successCount = 0;
    let failedCount = 0;
    const succeededIds = [];

    toast.info("جاري تحويل المحدد إلى قديم... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-old",
    });

    for (const order of selected) {
      try {
        await updateOrderStatus(
          order.orderNumber,
          "Old",
          "تم التحويل لقديم من Incomplete"
        );
        succeededIds.push(order.orderNumber);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    if (succeededIds.length > 0) {
      setLoadedOrders((prev) =>
        prev.filter((order) => !succeededIds.includes(order.orderNumber))
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    toast.dismiss("marking-old");
    setIsMarkingOld(false);

    if (successCount === 0) {
      toast.error(
        `فشل تحويل الطلبات إلى قديم` +
          (failedCount > 0 ? ` (${failedCount} طلب)` : "")
      );
      return;
    }

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى قديم بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const handleOrderEdited = async (updatedOrder) => {
    if (!updatedOrder?.orderId) {
      await loadInitialOrders();
      return;
    }
    try {
      const [enriched] = await enrichOrders([updatedOrder]);
      setLoadedOrders((prev) =>
        prev.map((o) => (o.orderNumber === enriched.orderNumber ? enriched : o))
      );
    } catch (err) {
      console.error(err);
      await loadInitialOrders();
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}${order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""}`;
    const formattedState = `${order.state} (D)`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;
    copyToClipboard(text);
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      const itemNum = `${index + 1}-${order.items.length}`;
      const type = item.type || "Unknown Type";
      const driveLink = item.driveLink || "No Link";
      return `${itemNum}\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
    }).join('\n');
    copyToClipboard(lines);
  };

    const handleCopyAllExcelDataNoReference = async (order) => {
    try {
      // Format phone number
      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""
      }`;
      
      const formattedState = `${order.state} (D)`;
      
      // No reference field - using empty string or tab
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      await copyOrderToTeePlus(finalText, order.orderNumber);
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      if (err.code === "SOURCE_FAILED") {
        toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      } else {
        toast.error("فشل في النسخ، حاول مرة تانية");
      }
    }
  };

  const handleCopyAllVisibleOrders = async () => {
    const nonCustomOrders = filteredOrders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) للنسخ");
      return;
    }

    const confirmCopy = window.confirm(
      `هل تريد نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة مرة واحدة إلى الإكسيل؟\n\n(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\nسيتم فحص التكرار + زيادة الرقم المرجع تلقائيًا`
    );
    if (!confirmCopy) return;

    let finalLines = [];
    let hasDuplicate = false;
    let currentRef = currentReference;

    for (const order of nonCustomOrders) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );

        if (count > 0) {
          hasDuplicate = true;
          finalLines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
        }

        const phone = `${order.phoneNumber}${
          order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""
        }`;
        const formattedState = `${order.state} (D)`;
        const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

        const items = order.items;
        const orderLines = [];

        items.forEach((item, index) => {
          const itemNum = `${index + 1}-${items.length}`;
          const itemData = teePlusPackedItemData(item);
          const itemLine = `${itemNum}${itemData}`;

          if (index === 0) {
            orderLines.push(`${customerBase}\t\t${itemLine}`);
          } else {
            orderLines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
          }
        });

        finalLines.push(...orderLines);

        const match = currentRef.match(/(\d+)$/);
        if (match) {
          const num = parseInt(match[0], 10);
          currentRef = currentRef.replace(/\d+$/, (num + 1).toString());
        }
      } catch (err) {
        finalLines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
      }
    }

    setCurrentReference(currentRef);
    setReferenceInput(currentRef);
    localStorage.setItem("orderReference", currentRef);

    const finalText = finalLines.join("\n");
    try {
      await copyOrderToTeePlus(finalText, nonCustomOrders.map((o) => o.orderNumber));
      toast.success(
        `تم نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة بنجاح!${hasDuplicate ? " (يوجد تكرار في بعض الأرقام)" : ""}`,
        { autoClose: 6000 }
      );
    } catch (err) {
      if (err.code === "SOURCE_FAILED") {
        toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      } else {
        toast.error("فشل في النسخ، حاول مرة تانية");
      }
    }
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const isOrderCustom = (order) =>
    order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));

  const isOrderNonCustom = (order) => !isOrderCustom(order);

  const filteredOrders = loadedOrders.filter((order) => {
    if (designFilter === "custom" && !isOrderCustom(order)) return false;
    if (designFilter === "normal" && !isOrderNonCustom(order)) return false;

    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />

      {editingOrderId && (
        <EditOrderModal
          orderId={editingOrderId}
          onClose={() => setEditingOrderId(null)}
          onSaved={handleOrderEdited}
        />
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Printer className="text-slate-700" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-700 to-cyan-600 bg-clip-text text-transparent">
              Incomplete
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate("/follow-up-phones")}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
            >
              <Phone size={20} />
              متابعة WhatsApp
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
            >
              <ChevronRight size={20} />
              العودة للرئيسية
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {canViewOrders(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Package className="text-amber-700" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص Incomplete</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-slate-700">{incompleteOrdersSummary.count} طلبات</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد القطع</span>
                    <span className="text-2xl font-bold text-indigo-600">{incompleteOrdersSummary.totalProductsSold ?? 0} قطعة</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{incompleteOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-slate-300 focus:border-slate-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                showFilters || designFilter !== "all"
                  ? "bg-slate-50 border-slate-400 text-slate-700"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Filter size={20} />
              تصفية
              {designFilter !== "all" && (
                <span className="bg-slate-600 text-white text-xs px-2 py-0.5 rounded-full">1</span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
              <span className="text-sm text-gray-600 ml-2">نوع التصميم:</span>
              {[
                { value: "all", label: "كل الطلبات" },
                { value: "custom", label: "طلبات مخصصة (CUSTOM)" },
                { value: "normal", label: "طلبات عادية" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setDesignFilter(value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    designFilter === value
                      ? value === "custom"
                        ? "bg-green-600 text-white shadow-md"
                        : value === "normal"
                          ? "bg-gray-600 text-white shadow-md"
                          : "bg-slate-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bulk actions */}
        <div className="bg-gradient-to-r from-cyan-50 to-slate-50 backdrop-blur-sm rounded-2xl shadow-xl p-4 md:p-6 mb-6 border-2 border-cyan-300">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-cyan-100 rounded-xl">
                <CheckSquare className="text-cyan-700" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-cyan-900 text-lg">إجراءات المحدد</h3>
                <p className="text-sm text-cyan-700">
                  المحدد: <span className="font-bold">{selectedOrderNumbers.size}</span> طلب
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setSelectAllVisible(true, filteredOrders)}
                disabled={filteredOrders.length === 0 || isMarkingGirabatcom || isMarkingOld}
                className="bg-white border-2 border-cyan-400 text-cyan-800 font-bold px-5 py-3 rounded-xl hover:bg-cyan-50 disabled:opacity-50 transition-all flex items-center gap-2"
              >
                <CheckSquare size={18} />
                تحديد الكل
              </button>
              <button
                type="button"
                onClick={() => setSelectAllVisible(false, filteredOrders)}
                disabled={selectedOrderNumbers.size === 0 || isMarkingGirabatcom || isMarkingOld}
                className="bg-white border-2 border-gray-300 text-gray-700 font-medium px-5 py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all"
              >
                إلغاء التحديد
              </button>
              <button
                type="button"
                onClick={handleCopySelectedPhones}
                disabled={selectedOrderNumbers.size === 0 || isMarkingGirabatcom || isMarkingOld}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ أرقام الموبايل ({selectedOrderNumbers.size})
              </button>
              {canAct && (
                <>
                <button
                  type="button"
                  onClick={handleMarkSelectedAsGirabatcom}
                  disabled={
                    isOrdersLoading ||
                    selectedOrderNumbers.size === 0 ||
                    isMarkingGirabatcom ||
                    isMarkingOld
                  }
                  className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isMarkingGirabatcom ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      جاري التحويل...
                    </>
                  ) : (
                    <>
                      <Building2 size={18} />
                      تحويل لـ Girabatcom ({selectedOrderNumbers.size})
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleMarkSelectedAsOld}
                  disabled={
                    isOrdersLoading ||
                    selectedOrderNumbers.size === 0 ||
                    isMarkingGirabatcom ||
                    isMarkingOld
                  }
                  className="bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isMarkingOld ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      جاري التحويل...
                    </>
                  ) : (
                    <>
                      <Archive size={18} />
                      تحويل لقديم ({selectedOrderNumbers.size})
                    </>
                  )}
                </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-6 border-2 border-green-300">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <CopyCheck className="text-green-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-green-900 text-xl">نسخ جماعي للإكسيل بالمرجع</h3>
                <p className="text-sm text-green-700">
                  نسخ الطلبات غير المخصصة مع المرجع الحالي: {currentReference}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCopyAllVisibleOrders}
              disabled={isOrdersLoading || filteredOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Copy size={20} />
              نسخ الكل (TeePlus)
            </button>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-6 border border-white/30">
          <div className="flex items-center gap-3 mb-4">
            <Hash className="text-indigo-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">الرقم الحالي</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num + 1;
                      const newRef = currentReference.replace(/\d+$/, newNum.toString());
                      setCurrentReference(newRef);
                      setReferenceInput(newRef);
                      localStorage.setItem("orderReference", newRef);
                    }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-r-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="زيادة الرقم"
                >
                  <Plus size={20} />
                </button>
                <div className="flex-1 text-3xl font-bold text-indigo-600 bg-indigo-50 py-3 px-4 text-center border-2 border-indigo-200">
                  {currentReference}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num - 1;
                      if (newNum >= 0) {
                        const newRef = currentReference.replace(/\d+$/, newNum.toString());
                        setCurrentReference(newRef);
                        setReferenceInput(newRef);
                        localStorage.setItem("orderReference", newRef);
                      }
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-l-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="إنقاص الرقم"
                >
                  <Minus size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={() => {
                  if (referenceInput.trim() && referenceInput.includes(".")) {
                    setCurrentReference(referenceInput.trim());
                    localStorage.setItem("orderReference", referenceInput.trim());
                    toast.success("تم تحديث رقم المرجع!");
                  }
                }}
                className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
                placeholder="Girabatcom.4000"
                style={{ fontFamily: "cairo black !important" }}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  setReferenceInput(referenceInput.trim());
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold flex items-center gap-2"
            >
              <Save size={20} />
              تحديث الرقم
            </button>
            <button
              type="button"
              onClick={handleCopyAllVisibleOrders}
              disabled={isOrdersLoading || filteredOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Copy size={20} />
              نسخ الكل (TeePlus) ({filteredOrders.length})
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-4 text-right">
            الرقم المرجع مشترك مع صفحة Confirmed، ويزيد تلقائيًا بعد النسخ الجماعي
          </p>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1200px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-50 to-cyan-50 border-b-2 border-slate-200">
                        <th className="p-2 text-center font-bold text-slate-800">
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-cyan-600"
                              checked={
                                filteredOrders.length > 0 &&
                                filteredOrders.every((o) =>
                                  selectedOrderNumbers.has(o.orderNumber)
                                )
                              }
                              onChange={(e) =>
                                setSelectAllVisible(e.target.checked, filteredOrders)
                              }
                            />
                            <span className="text-xs">الطلب</span>
                          </div>
                        </th>
                        <th className="p-4 text-right font-bold text-slate-800">العميل</th>
                        <th className="p-4 text-right font-bold text-slate-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-slate-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-slate-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-slate-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-slate-800">نسخ</th>
                        <th className="p-4 text-center font-bold text-slate-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-slate-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          {/* Order Number + Checkbox */}
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <input
                                type="checkbox"
                                className="w-5 h-5 accent-cyan-600"
                                checked={selectedOrderNumbers.has(order.orderNumber)}
                                onChange={() => toggleSelectOrder(order.orderNumber)}
                              />
                              <span className="font-mono font-bold text-slate-700 text-sm">#{order.orderNumber}</span>
                            </div>
                          </td>

                          {/* Customer Name */}
                          <td className="p-4">
                            {isAdmin(authState.role) && (
                                <button
                                  onClick={() => setEditingOrderId(order.orderNumber)}
                                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-all mb-5"
                                >
                                  <div className="flex items-center justify-center gap-1">
                                    <Edit size={14} />
                                    تعديل
                                  </div>
                                </button>
                            )}
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-medium">{order.name}</span>
                            </div>

                          {/* Phone Numbers */}
                          <br />
                          <div className="flex items-center gap-2">
                          <Phone size={16} className="text-green-600" />

                          <div className="flex flex-col">
                            <span
                              onClick={() => navigator.clipboard.writeText(order.phoneNumber)}
                              className="text-green-600 hover:underline cursor-pointer"
                              title="Copy phone number"
                            >
                              {order.phoneNumber}
                            </span>

                            {order.phoneNumber2 !== "غير معروف" && (
                              <span
                                onClick={() => navigator.clipboard.writeText(order.phoneNumber2)}
                                className="text-green-600 hover:underline cursor-pointer"
                                title="Copy phone number"
                              >
                                {order.phoneNumber2}
                              </span>
                            )}
                          </div>
                        </div>
                          </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily : "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                             <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-slate-700">{order.state}</span>
                            </div>
                          </td>

                         

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          {/* Products + Drive Links - Merged */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-slate-700">{item.name}</div>
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                       {!item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                      
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-slate-600 hover:text-slate-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() =>
                                                handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                              }
                                              className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                              title="تحميل الصورة"
                                              style={{
                                                backgroundColor: "#15d225",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              <Download size={16} />
                                              <span className="text-sm">Download</span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {index < order.items.length - 1 && (
                                  <hr className="my-2 border-gray-200" />
                                )}
                              </div>
                            ))}
                          </td>

                          {/* Notes */}
                          <td className="p-4 max-w-[150px]">
                            <p style={{fontFamily:"cairo black"}} className="text-[16px]">{order.note || "—"}</p>
                          </td>

                          {/* Copy Buttons */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleCopyOrderInfo(order)}
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={14} />
                                العميل
                              </button>
                              <button
                                onClick={() => handleCopyItems(order)}
                                className="bg-gradient-to-r from-slate-500 to-cyan-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Layers size={14} />
                                المنتجات
                              </button>
                              <button
                                onClick={() => handleCopyAllExcelDataNoReference(order)}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={16} />
                                نسخ (TeePlus)
                              </button>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="p-2">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                                className="bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle size={14} />
                                  ارجاع الطلب اي مؤكد
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                className="bg-gray-600 text-white px-6 py-4 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Girabatcom")}
                                className="bg-cyan-500 text-white px-6 py-4 rounded-lg hover:bg-cyan-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Building2 size={14} />
                                  Girabatcom
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printing Hold")}
                                className="bg-slate-700 text-white px-6 py-4 rounded-lg hover:bg-slate-800 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Printer size={14} />
                                  Printing Hold
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printlet")}
                                className="bg-fuchsia-600 text-white px-6 py-4 rounded-lg hover:bg-fuchsia-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Send size={14} />
                                  Printlet
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>

                          {/* Delete */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-slate-700 to-cyan-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Printer className="mx-auto mb-4 text-gray-400" size={64} />
                  {loadedOrders.length > 0 ? (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات تطابق التصفية</p>
                      <p className="text-lg text-gray-500">جرّب تغيير نوع التصميم أو البحث</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات في Incomplete</p>
                      <p className="text-lg text-gray-500">سيتم عرض الطلبات المعلّقة للطباعة هنا</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncompleteOrdersPage;